/**
 * ListingsDataGrid Component for Bay Email Editor
 *
 * Self-contained data grid component for displaying listings with ka-table.
 * Supports selection (single/multiple) and custom cell components.
 *
 * This is a simplified version optimized for use within the email editor packages.
 * Source: ui/src/components/ListingsDataGrid
 */

import React, { useEffect, useState, useMemo } from 'react';
import Form from 'react-bootstrap/Form';
import { kaReducer, Table } from 'ka-table';
import { ActionType, SortDirection } from 'ka-table/enums';
import { kaPropsUtils } from 'ka-table/utils';
import { deselectAllRows, deselectRow, selectRow, selectAllFilteredRows, deselectAllFilteredRows } from 'ka-table/actionCreators';
import './ListingsDataGrid.scss';
import Skeleton from 'react-loading-skeleton';
import { BsSortDown, BsSortUp } from 'react-icons/bs';

interface SelectionCellProps {
	rowKeyValue: string;
	dispatch: (action: any) => void;
	isSelectedRow: boolean;
	selectedRows: string[];
	selectionMode?: 'single' | 'multiple';
}

const SelectionCell: React.FC<SelectionCellProps> = ({
	rowKeyValue,
	dispatch,
	isSelectedRow,
	selectedRows,
	selectionMode = 'multiple'
}) => {
	if (selectionMode === 'single') {
		return (
			<Form.Check
				className="bay-checkbox ms-1 mt-0 p-1"
				type="radio"
			>
				<Form.Check.Input
					className="mt-0"
					type="radio"
					name="listing-selection"
					checked={isSelectedRow}
					onChange={() => {
						dispatch(deselectAllRows());
						dispatch(selectRow(rowKeyValue));
					}}
				/>
			</Form.Check>
		);
	}

	return (
		<Form.Check
			checked={isSelectedRow}
			className="bay-checkbox ms-1 mt-0"
			type="checkbox"
			onChange={(event) => {
				if (event.currentTarget.checked) {
					dispatch(selectRow(rowKeyValue));
				} else {
					dispatch(deselectRow(rowKeyValue));
				}
			}}
		/>
	);
};

interface SelectionHeaderProps {
	dispatch: (action: any) => void;
	areAllRowsSelected: boolean;
	selectionMode?: 'single' | 'multiple';
}

const SelectionHeader: React.FC<SelectionHeaderProps> = ({
	dispatch,
	areAllRowsSelected,
	selectionMode = 'multiple'
}) => {
	if (selectionMode === 'single') {
		return null;
	}

	return (
		<Form.Check
			checked={areAllRowsSelected}
			className="bay-checkbox ms-1 mt-0"
			type="checkbox"
			onChange={(event) => {
				if (event.currentTarget.checked) {
					dispatch(selectAllFilteredRows());
				} else {
					dispatch(deselectAllFilteredRows());
				}
			}}
		/>
	);
};

interface ListingsDataGridProps {
	data: any[];
	initTableProps: any;
	enableRowSelection?: boolean;
	selectionMode?: 'single' | 'multiple';
	setSelectedData?: (selected: string[]) => void;
	onSortChange?: (sort: { sortBy: string; orderDirection: 'ASC' | 'DESC' }) => void;
	loading?: boolean;
	t: (key: string) => string;
	selectedListing?: string[];
	hasSearch?: boolean;
	hasFilters?: boolean;
}

const BORDERED_COLUMN_KEYS = ['address', 'status', 'price', 'propertyType', 'listingAgent'];

const ListingsDataGrid: React.FC<ListingsDataGridProps> = ({
	data,
	initTableProps,
	enableRowSelection = false,
	selectionMode = 'multiple',
	setSelectedData,
	onSortChange,
	loading = false,
	t,
	selectedListing = [],
	hasSearch = false,
	hasFilters = false,
}) => {
	const [tableProps, changeTableProps] = useState<any>();

	const tableDispatch = (action: any) => {
		if (action?.type === ActionType.UpdateSortDirection && onSortChange && tableProps?.columns?.length) {
			const targetColumn = tableProps.columns.find((column: any) => column?.key === action?.columnKey);
			if (targetColumn?.sortBy) {
				const nextDirection = action?.sortDirection === SortDirection.Ascend
					? 'ASC'
					: action?.sortDirection === SortDirection.Descend
						? 'DESC'
						: targetColumn?.sortDirection === SortDirection.Ascend
							? 'DESC'
							: 'ASC';
				onSortChange({
					sortBy: targetColumn.sortBy,
					orderDirection: nextDirection,
				});
			}
		}
		changeTableProps((prevState: any) => kaReducer(prevState, action));
	};

	const selectedData = useMemo(() => {
		if (tableProps && tableProps?.data?.length) {
			return tableProps?.selectedRows;
		}
		return [];
	}, [tableProps]);

	useEffect(() => {
		if (setSelectedData && selectedData) {
			setSelectedData(selectedData);
		}
	}, [selectedData, setSelectedData]);

	useEffect(() => {
		if (initTableProps) {
			let tableColumns = initTableProps.columns;
			if (enableRowSelection) {
				tableColumns = [
					{ key: 'selection-cell', width: 40, isEditable: false, title: '' },
					...initTableProps.columns,
				];
			}
			changeTableProps({
				...initTableProps,
				columns: tableColumns,
				data,
				selectedRows: selectedListing || [],
			});
		}
	}, [data, initTableProps, enableRowSelection]);

	if (!tableProps) return null;

	return (
		<div className="bay-data-grid shadow-none">
			<Table
				{...tableProps}
				childComponents={{
					dataRow: {
						elementAttributes: ({ rowKeyValue, isSelectedRow }: any) => {
							if (!enableRowSelection) return {};
							return {
								style: { cursor: 'pointer' },
								onClick: (event: React.MouseEvent) => {
									// The selection cell dispatches on its own; letting the row
									// handle that click too would toggle a checkbox straight back
									// off. Anything else interactive in a cell keeps its own click.
									const target = event.target as HTMLElement;
									if (target.closest('input, button, a, label')) return;

									if (selectionMode === 'single') {
										tableDispatch(deselectAllRows());
										tableDispatch(selectRow(rowKeyValue));
										return;
									}
									tableDispatch(
										isSelectedRow ? deselectRow(rowKeyValue) : selectRow(rowKeyValue),
									);
								},
							};
						},
					},
					noDataRow: {
						content: () => {
							if (loading) {
								//NOTE: Array.from() to generate 10 skeleton rows for a better user experience.
								return Array.from({ length: 10 }).map((_, i) => (
									<div key={i} className="d-flex justify-content-between align-items-center p-2 px-2">
										<div className="d-flex gap-3 justify-content-center align-items-center">
											<Skeleton height={20} width={20} />
											<Skeleton height={36} width={48} />
											<Skeleton height={36} width={200} />
										</div>
										<Skeleton height={32} width={116} />
										<Skeleton height={32} width={116} />
										<Skeleton height={24} width={116} />
										<Skeleton height={24} width={116} />
										<Skeleton height={24} width={116} />
									</div>
								));
							}

							// Empty state messaging varies based on search/filter state
							return (
								<div className="d-flex flex-column align-items-center justify-content-center py-4 gap-4">
									<div>{t(hasSearch ? 'No results found' : hasFilters ? "We couldn't find any listings matching your current filters." : "Looks like there are no listings yet.")}</div>
									{!hasSearch && <img
										 alt='Listings empty state'
										className='my-2'
										height={190}
										src='https://s3.us-west-1.wasabisys.com/bay/client/ListingsEmptyState.png'
										width={260}
									/>}
								</div>
							);
						},
					},
					headCell: {
						elementAttributes: (props: any) => {
							if (props.column.key === 'selection-cell') {
								// For single selection, keep header visible but empty for column alignment
								if (selectionMode === 'single') {
									return {
										className: 'selection-header-cell',
										style: {
											display: 'flex',
											padding: '0.75rem',
											alignItems: 'center',
											justifyContent: 'center',
											borderBottom: 'none',
											borderRight: '0',
											borderTop: '0',
											borderLeft: '0',
											width: '40px'
										}
									};
								}
								// For multiple selection, show the checkbox header
								return {
									className: 'selection-header-cell',
									style: {
										display: 'flex',
										padding: '0.75rem',
										alignItems: 'center',
										justifyContent: 'center',
										borderBottom: 'none',
										borderRight: '0',
										borderTop: '0',
										borderLeft: '0'
									}
								};
							}
							const hasRightBorder = BORDERED_COLUMN_KEYS.includes(props.column.key);
							return {
								style: {
									color: 'var(--bs-secondary)',
									fontWeight: '600',
									padding: '0.75rem',
									borderBottom: '1px solid var(--bs-border-color)',
									borderRight: hasRightBorder ? '1px solid var(--bs-border-color)' : '0',
									borderTop: '0',
									borderLeft: '0',
									backgroundColor: 'transparent',
									width: props.column.width ? props.column.width : 'auto'
								}
							};
						},
						content: (props: any) => {
							if (props.column.key === 'selection-cell') {
								if (selectionMode === 'multiple' && tableProps?.data?.length) {
									return (
										<SelectionHeader
											{...props}
											areAllRowsSelected={tableProps?.data?.length > 0 ? kaPropsUtils?.areAllFilteredRowsSelected(tableProps) : false}
											selectionMode={selectionMode}
										/>
									);
								}
								// Return empty div for single-select to maintain column alignment
								return <div></div>;
							}
							return null;
						},
					},
					cell: {
						elementAttributes: (props: any) => {
							if (props.column.customStyles) {
								return {
									style: props.column.customStyles
								};
							}
							if (props.column.key === 'selection-cell') {
								return {
									style: {
										display: 'flex',
										padding: 0,
										alignItems: 'center',
										justifyContent: 'center',
										borderBottom: '0',
										borderRight: '0',
										borderTop: '0',
										borderLeft: '0',
										width: '40px'
									}
								};
							}
							const hasRightBorder = BORDERED_COLUMN_KEYS.includes(props.column.key);
							return {
								style: {
									borderBottom: '0',
									borderRight: hasRightBorder ? '1px solid var(--bs-border-color)' : '0',
									borderTop: '0',
									borderLeft: '0',
									width: props.column.width ? props.column.width : 'auto'
								}
							};
						},
					},
					cellText: {
						content: (props: any) => {
							if (props.column.customComponent) {
								const Component = props.column.customComponent;
								return <Component {...props} t={t} />;
							}
							if (props.column.key === 'selection-cell') {
								return <SelectionCell {...props} selectionMode={selectionMode} />;
							}
							return null;
						}
					},
					sortIcon: {
						content: ({ column }) => {
							return (
								<span
									style={{
										width: 12,
										position: 'relative',
										top: 1,
										paddingRight: '0.5rem',
									}}
								>
									{column?.sortDirection === SortDirection.Ascend ? (
										<BsSortUp color="#5d5f69" size={16} />
									) : (
										<BsSortDown color="#5d5f69" size={16} />
									)}
								</span>
							);
						},
					},
				}}
				dispatch={tableDispatch}
				loading={{
					enabled: loading && Boolean(tableProps?.data?.length),
					text: t('Loading data...'),
				}}
			/>
		</div>
	);
};

export default ListingsDataGrid;
